import { LitElement, TemplateResult } from 'lit';
import { property, query } from 'lit/decorators.js';
import { localized } from '@lit/localize';
import { updateLocale } from '../../localization/config';
import { LocaleCode } from '../../types/lang.types';
import type { RoundtripOfferViewModel } from '../../types/offer.types';
import { renderPriceBoxDefault } from '../../templates/price-box-default.template';
import { renderPriceBoxFixed } from '../../templates/price-box-fixed.template';
import { getTranslationBy } from '../../localization/translation-mapping';
import { AnalyticsService } from '../../services/analytics.service';

@localized()
export class PriceBox extends LitElement {
    protected override createRenderRoot(): HTMLElement | DocumentFragment {
        return this; // Light DOM – render inside parent's Shadow DOM
    }

    @property({ type: String })
    public lang = LocaleCode.DE_CH;

    @property({ type: String })
    public currency = 'CHF';

    @property({ type: String })
    public brand = 'kuoni';

    @property({ type: String })
    public tenant = 'kuonireisen-ch';

    @property({ type: Object, attribute: 'additional-tracking-data' })
    public additionalTrackingData = {};

    @property({ type: String, attribute: 'component-title' })
    componentTitle: string = '';

    @property({ type: Boolean, attribute: 'show-operator' })
    showOperator: boolean = false;

    @property({ type: String, attribute: 'operator-name' })
    operatorName: string = '';

    @property({ type: String, attribute: 'analytics-name' })
    analyticsName: string = '';

    @property({ type: String, attribute: 'price' })
    price: string = '';

    @property({ type: String, attribute: 'duration-string' })
    durationString: string = '';

    @property({ type: String, attribute: 'phone-number' })
    phoneNumber: string = '';

    @property({ type: Boolean, attribute: 'fixed-mode' })
    fixedMode: boolean = false;

    @property({ type: String, attribute: 'roundtrip-id' })
    roundtripId: string = '';

    @property({ type: String, attribute: 'roundtrip-key' })
    roundtripKey: string = '';

    @property({ type: String, attribute: 'roundtrip-types' })
    roundtripTypes: string = '';

    @property({ type: Object })
    bestOfferData: RoundtripOfferViewModel | null | undefined = null;

    @property({ type: String, attribute: 'start-end-destination' })
    startDestination: string = '';

    @property({ type: String, attribute: 'start-point' })
    startPoint: string = '';

    @property({ type: String, attribute: 'languages' })
    languages: string = '';

    @property({ type: String, attribute: 'group-size' })
    groupSize: string = '';

    @property({ type: String, attribute: 'best-travel-time' })
    bestTravelTime: string = '';

    @property({ type: Boolean, attribute: 'loading' })
    loading: boolean = false;

    @query('.m-price-box')
    priceBoxElement?: HTMLElement;

    async connectedCallback(): Promise<void> {
        super.connectedCallback();
        const langAttribute = this.lang ?? 'de';
        await updateLocale(langAttribute);
    }

    updated(changedProperties: Map<string, unknown>): void {
        super.updated(changedProperties);

        // Handle language changes at runtime
        if (changedProperties.has('lang')) {
            const newLang = this.lang ?? 'de';
            console.log(`Language changed to: ${newLang}`);
            updateLocale(newLang).then(() => {
                this.requestUpdate();
            });
        }
    }

    private _onButtonClick(event: PointerEvent): void {
        event.stopPropagation();
        event.preventDefault();
        this._dispatchAnalyticsEvent(false);
        this.emitEvent('der-price-box-click', this.bestOfferData);
    }

    private _onPhoneClick(event: PointerEvent): void {
        event.stopPropagation();
        this._dispatchAnalyticsEvent(true);
    }

    private _dispatchAnalyticsEvent(phoneButton: boolean): void {
        const eventData = AnalyticsService.buildOfferPanelEvent(
            phoneButton ? 'cta_phonenumber' : 'cta_start_request',
            this.id ?? '',
            phoneButton ? 'init roundtrip offer request call' : 'start roundtrip offer request',
            'roundtrip offer request',
            this.lang
        );
        AnalyticsService.dispatch(this, eventData);
    }

    protected emitEvent(name: string, detail: unknown): void {
        this.dispatchEvent(new CustomEvent(name, { detail, bubbles: true, composed: true }));
    }

    override render(): TemplateResult | undefined {
        let operatorName = '';
        if (this.showOperator) {
            operatorName = getTranslationBy(this.brand);
        }
        const templateOptions = {
            componentTitle: this.componentTitle,
            operatorName: operatorName,
            phoneNumber: this.phoneNumber,
            roundtripTypes: this.roundtripTypes,
            startDestination: this.startDestination,
            startPoint: this.startPoint,
            languages: this.languages,
            groupSize: this.groupSize,
            bestTravelTime: this.bestTravelTime,
            roundtripKey: this.roundtripKey,
            price: this.price,
            durationString: this.durationString,
            loading: this.loading,
            onButtonClick: this._onButtonClick.bind(this),
            onPhoneClick: this._onPhoneClick.bind(this),
        };

        if (this.fixedMode) {
            return renderPriceBoxFixed(templateOptions, this.lang);
        }

        return renderPriceBoxDefault(templateOptions);
    }
}
